import Vue from 'vue';
import zenscroll from 'zenscroll';
import Errors from '../classes/errors';
import {
    checkCCM19Consent,
    checkOneTrustConsent,
    setCaptchaEuScript,
} from '../utils';
import { getFormRenderToken } from '../services/anti-bot';
import { isCaptchaEnabled } from '../services/captcha';

Vue.component('contest-form-component', {
    props: {
        captchaEuPublicKey: {
            value: String,
        },
    },

    /**
     * Vue.js instance reactive data variables.
     *
     * @return {Object}
     */
    data() {
        return {
            formRenderToken: '',
            errors: new Errors(),
            formCompleted: false,
            submitting: false,
            isNumberValid: true,
        };
    },

    computed: {
        toggleLoadingButtonClass() {
            return this.submitting ? 'button--loading' : '';
        },
    },

    /**
     * Called when the Vue.js instance is mounted and
     * ready for use.
     *
     * @return {void}
     */
    mounted() {
        setCaptchaEuScript();

        getFormRenderToken().then((token) => {
            this.formRenderToken = token;
        });
    },

    /**
     * Vue.js instance methods.
     *
     * @type {Object}
     */
    methods: {
        handleMarketingConsentChange(e) {
            e.preventDefault();
            const isConsentChecked = e.target.checked;
            const hiddenMarketingInput = document.querySelector('#marketing');
            if (!hiddenMarketingInput) return;
            hiddenMarketingInput.setAttribute(
                'value',
                isConsentChecked ? '1' : '0',
            );
        },

        scrollToFirstError() {
            Vue.nextTick(() => {
                let firstError = document.querySelector(
                    '.new-input__container--error',
                );
                zenscroll.center(firstError);
            });
        },

        scrollToTop() {
            Vue.nextTick(() => {
                zenscroll.toY(0);
            });
        },

        async onSubmit(event) {
            event.preventDefault();
            const form = event.currentTarget;
            let dataFromForm = new FormData(form);
            const data = {};

            for (let [key, val] of dataFromForm.entries()) {
                Object.assign(data, { [key]: val });
            }

            const filteredData = Object.keys(data).reduce((acc, key) => {
                if (!['redirectUrl'].includes(key)) {
                    acc[key] = data[key];
                }
                return acc;
            }, {});

            const dataLayerData = {
                event: 'lead',
                lead_type: 'Contest',
                country: data.country,
                user_data: {
                    ...filteredData,
                },
            };

            if (this.submitting) {
                return;
            }

            this.submitting = true;

            try {
                if (!(await isCaptchaEnabled())) {
                    await this.handleDataSubmit(form, dataLayerData);
                    return;
                }

                const captchaSolution = await KROT.getSolution(
                    this.captchaEuPublicKey,
                );

                await this.handleDataSubmit(
                    form,
                    dataLayerData,
                    captchaSolution,
                );
            } catch (e) {
                throw new Error(e);
            } finally {
                this.submitting = false;
            }
        },

        async handleDataSubmit(form, dataLayerData, captchaSolution) {
            try {
                const formData = new FormData(form);
                formData.append('formRenderToken', this.formRenderToken);
                if (captchaSolution) {
                    formData.append(
                        'captchaSolution',
                        JSON.stringify(captchaSolution),
                    );
                }
                const response = await axios.post('/', formData);
                const result = response.data;

                if (result.data.errors) {
                    this.errors.clear();
                    this.errors.record(result.data.errors);

                    this.scrollToFirstError();
                    this.submitting = false;
                    return;
                }

                this.sendGTMData(dataLayerData);

                // Success
                this.formCompleted = true;
                this.errors.clear();

                window.location = result.data.redirectUrl;
                this.scrollToTop();
            } catch (e) {
                throw new Error(e);
            }
        },

        sendGTMData(dataToPush) {
            if (!dataToPush) return;
            if (checkCCM19Consent() || checkOneTrustConsent()) {
                // check if you are on production-enviroment
                if (!window.dataLayer) {
                    window.dataLayer = window.dataLayer || [];
                }

                window.dataLayer.push(dataToPush);
            }
        },
    },
});
